import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import { useTranslation } from "react-i18next";
import { createStore, uploadStoreLogo, uploadStoreCoverImage } from "../../api/storeService";
import { searchCities } from "../../api/cityService";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const MAX_COVER_BYTES = 8 * 1024 * 1024;

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return fallback;
}

export default function CreateStoreDialog({ open, onClose, onCreated }) {
  const { t } = useTranslation(["boStores", "boStoreProfile", "boShared", "common"]);

  const [storeName, setStoreName] = useState("");
  const [storeNameArabic, setStoreNameArabic] = useState("");
  const [description, setDescription] = useState("");
  const [addressText, setAddressText] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [locating, setLocating] = useState(false);

  const [cityInput, setCityInput] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetForm() {
    setStoreName("");
    setStoreNameArabic("");
    setDescription("");
    setAddressText("");
    setLatitude("");
    setLongitude("");
    setCityInput("");
    setSelectedCity(null);
    setCityOptions([]);
    setError("");
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setLogoFile(null);
    setLogoPreview("");
    setCoverFile(null);
    setCoverPreview("");
  }

  function handleClose() {
    if (saving) return;
    resetForm();
    onClose();
  }

  function handleLogoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError(t("boShared:invalidImageType"));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError(t("boStoreProfile:logoTooLarge"));
      return;
    }
    setError("");
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }

  function handleCoverChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError(t("boShared:invalidImageType"));
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      setError(t("boStoreProfile:coverTooLarge"));
      return;
    }
    setError("");
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }

  function handleCityInputChange(value) {
    setCityInput(value);
    if (!value.trim()) {
      setCityOptions([]);
      return;
    }
    setCityLoading(true);
    searchCities(value.trim(), 15)
      .then(setCityOptions)
      .catch(() => setCityOptions([]))
      .finally(() => setCityLoading(false));
  }

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      setError(t("boStoreProfile:geoUnavailable"));
      return;
    }
    setError("");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setLocating(false);
      },
      () => {
        setError(t("boStoreProfile:geoError"));
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmedName = storeName.trim();
    if (!trimmedName) {
      setError(t("boStoreProfile:nameRequiredError"));
      return;
    }

    setSaving(true);
    setError("");
    try {
      let created = await createStore({
        storeName: trimmedName,
        storeNameArabic: storeNameArabic.trim() || null,
        description: description.trim() || null,
        city: selectedCity?.nameHebrew ?? null,
        addressText: addressText.trim() || null,
        latitude: latitude !== "" ? Number(latitude) : null,
        longitude: longitude !== "" ? Number(longitude) : null,
      });

      try {
        if (logoFile) created = await uploadStoreLogo(created.storeId, logoFile);
        if (coverFile) created = await uploadStoreCoverImage(created.storeId, coverFile);
      } catch {
        // Store was created successfully; the owner can still add/retry images from the store profile page.
      }

      resetForm();
      onCreated(created);
    } catch (err) {
      setError(getErrorMessage(err, t("boStores:createError")));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {t("boStores:createStoreTitle")}
        <IconButton onClick={handleClose} size="small" disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {t("boStoreProfile:changeLogo")}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {logoPreview ? (
                    <Box component="img" src={logoPreview} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <StorefrontRoundedIcon sx={{ fontSize: 28, color: "text.secondary" }} />
                  )}
                </Box>
                <Button size="small" variant="outlined" onClick={() => logoInputRef.current?.click()} disabled={saving}>
                  {t("boStoreProfile:changeLogo")}
                </Button>
                <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoChange} disabled={saving} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {t("boStoreProfile:changeCover")}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 96,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {coverPreview ? (
                    <Box component="img" src={coverPreview} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <ImageRoundedIcon sx={{ fontSize: 28, color: "text.secondary" }} />
                  )}
                </Box>
                <Button size="small" variant="outlined" onClick={() => coverInputRef.current?.click()} disabled={saving}>
                  {t("boStoreProfile:changeCover")}
                </Button>
                <input ref={coverInputRef} type="file" accept="image/*" hidden onChange={handleCoverChange} disabled={saving} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t("boStoreProfile:storeName")}
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                fullWidth
                size="small"
                disabled={saving}
                autoFocus
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t("boStoreProfile:storeNameArabic")}
                value={storeNameArabic}
                onChange={(e) => setStoreNameArabic(e.target.value)}
                fullWidth
                size="small"
                disabled={saving}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label={t("boShared:description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={3}
                disabled={saving}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small"
                options={cityOptions}
                loading={cityLoading}
                value={selectedCity}
                inputValue={cityInput}
                filterOptions={(x) => x}
                getOptionLabel={(option) => option?.nameEnglish || option?.nameHebrew || ""}
                isOptionEqualToValue={(option, value) => option.nameHebrew === value?.nameHebrew}
                onInputChange={(_e, value) => handleCityInputChange(value)}
                onChange={(_e, value) => {
                  setSelectedCity(value);
                  setCityInput(value?.nameEnglish || value?.nameHebrew || "");
                }}
                disabled={saving}
                renderOption={(props, option) => (
                  <li {...props} key={option.cityId}>
                    {option.nameEnglish || option.nameHebrew}
                    {option.districtName ? ` · ${option.districtName}` : ""}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label={t("boStoreProfile:city")} />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t("boStoreProfile:address")}
                value={addressText}
                onChange={(e) => setAddressText(e.target.value)}
                fullWidth
                size="small"
                disabled={saving}
              />
            </Grid>
            <Grid size={12}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("boStoreProfile:mapLocationOptional")}
                </Typography>
                <Button
                  size="small"
                  startIcon={locating ? <CircularProgress size={14} /> : <MyLocationRoundedIcon fontSize="small" />}
                  onClick={handleUseCurrentLocation}
                  disabled={saving || locating}
                >
                  {t("boStoreProfile:useCurrentLocation")}
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t("boStoreProfile:latitude")}
                type="number"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                fullWidth
                size="small"
                disabled={saving}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t("boStoreProfile:longitude")}
                type="number"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                fullWidth
                size="small"
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            {t("common:cancel")}
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? t("boShared:saving") : t("boStores:createStore")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
